import { Injectable, Signal, computed, inject, signal } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, map, of, forkJoin, switchMap } from 'rxjs'
import {
    IActivity,
    ITask,
    IStatCard,
    ISummaryItem,
    IHighlight,
    IImportantDate,
    ILeaderboardGroup,
    IMonthlyLeaderboardApiItem,
    IMonthlyLeaderboardApiResponse,
    ISalesFunnelApiResponse,
    IDealsPipelineApiResponse,
    ISalesCycleOption,
    IUserApprovalsApiResponse,
} from '../../models/dashboard.model'
import { getAssetPath } from '../../constants/assets.constants'
import { environment } from 'src/environments/environment'
import {
    dashboardEndpoints,
    salesFunnelEndpoints,
    valueConstants,
    criticalApprovalsConstants,
} from '@app/core/constants/valueConstants'
import { ApprovalApiStatus } from '@app/core/constants/user-approvals.constants'
import { ELeaderboardType } from '@app/core/enum/leaderboard.enum'
import { UserStore } from '@core/services/user/user.store'
import { ESalesFunnelType } from '../../enum/sales-funnel.enum'
import { getCurrentAcademicYear } from '@app/core/constants/valueConstants'

@Injectable({
    providedIn: 'root',
})
export class IDashboardService {
    private readonly http = inject(HttpClient)
    private readonly userStore = inject(UserStore)
    private readonly apiBaseUrl = environment.apiBaseUrl

    private readonly now = () => new Date()
    private readonly _selectedAcademicYearStart = signal<number>(getCurrentAcademicYear(this.now()))

    readonly selectedAcademicYearStart = this._selectedAcademicYearStart.asReadonly()

    readonly salesCycleOptions = computed<ISalesCycleOption[]>(() => {
        const currentAcademicYear = getCurrentAcademicYear(this.now())
        const baseYear = currentAcademicYear - 2

        return Array.from({ length: 4 }, (_, i) => {
            const startYear = baseYear + i
            const endYear = startYear + 1

            return {
                startYear,
                endYear,
                label: `${startYear}-${String(endYear).slice(-2)}`,
            }
        })
    })

    readonly selectedSalesCycle: Signal<ISalesCycleOption> = computed(() => {
        const selected = this.selectedAcademicYearStart()
        const options = this.salesCycleOptions()
        return options.find((option) => option.startYear === selected) ?? options[2]
    })

    setSelectedAcademicYearStart(startYear: number): void {
        if (!Number.isFinite(startYear)) {
            return
        }
        this._selectedAcademicYearStart.set(Math.floor(startYear))
    }

    // Returns an array of statistics cards with icon, value, and label
    getStats(): Observable<IStatCard[]> {
        return this.http
            .get<{ stats: IStatCard[] }>(getAssetPath('MOCKS', 'stats.json'))
            .pipe(map((response) => response?.stats || []))
    }

    getActivities(): Observable<IActivity[]> {
        return this.http
            .get<{ activities: IActivity[] }>(getAssetPath('MOCKS', 'activities.json'))
            .pipe(map((response) => response?.activities || []))
    }

    // Returns an array of tasks with title, time, and completion status
    getTasks(): Observable<ITask[]> {
        return this.http
            .get<{ tasks: ITask[] }>(getAssetPath('MOCKS', 'tasks.json'))
            .pipe(map((response) => response?.tasks || []))
    }

    getSummaryItems(): Observable<ISummaryItem[]> {
        return this.http
            .get<{ summaryItems: ISummaryItem[] }>(getAssetPath('MOCKS', 'summary.json'))
            .pipe(map((response) => response?.summaryItems || []))
    }

    getHighlights(): Observable<IHighlight[]> {
        return this.http
            .get<{ highlights: IHighlight[] }>(getAssetPath('MOCKS', 'highlights.json'))
            .pipe(map((response) => response?.highlights || []))
    }

    getImportantDates(): Observable<IImportantDate[]> {
        return this.http
            .get<{ importantDates: IImportantDate[] }>(getAssetPath('MOCKS', 'dates.json'))
            .pipe(map((response) => response?.importantDates || []))
    }

    getCalendarEvents(): Observable<number[]> {
        return this.http
            .get<{ events: number[] }>(getAssetPath('MOCKS', 'calendar.json'))
            .pipe(map((response) => response?.events || []))
    }

    getLeaderboard(): Observable<ILeaderboardGroup[]> {
        const userId = this.userStore.getUserData()?.user_id
        if (!userId) {
            return of([])
        }

        const url =
            `${environment.apiBaseUrl}${dashboardEndpoints.MONTHLY_LEADERBOARD_PATH}`.replace(
                '{user_id}',
                encodeURIComponent(userId),
            )
        const urlWithAcademicYear = this.appendAcademicYearQueryParam(url, true)

        const monthLabel = new Date().toLocaleString('en-US', {
            month: 'short',
            year: 'numeric',
        })

        const buildGroup = (
            type: ELeaderboardType,
            _selector: (item: IMonthlyLeaderboardApiItem) => number,
        ): ILeaderboardGroup => {
            return {
                type,
                month: monthLabel,
                users: [],
            }
        }

        return this.http.get<IMonthlyLeaderboardApiResponse>(urlWithAcademicYear).pipe(
            map((response) => {
                const data = response?.data || []

                const toUsers = (
                    selector: (item: IMonthlyLeaderboardApiItem) => number,
                ): ILeaderboardGroup['users'] => {
                    const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' })
                    const sorted = [...data]
                        .map((item) => ({
                            id: item.user_id,
                            name: item.user_name,
                            score: selector(item),
                            rank: 0,
                        }))
                        .sort((user1, user2) => {
                            const scoreDiff = user2.score - user1.score
                            if (scoreDiff !== 0) {
                                return scoreDiff
                            }
                            return nameCollator.compare(user1.name ?? '', user2.name ?? '')
                        })

                    for (let index = 0; index < sorted.length; index++) {
                        sorted[index].rank = index + 1
                    }
                    return sorted
                }

                const groups: ILeaderboardGroup[] = [
                    {
                        ...buildGroup(ELeaderboardType.MOST_DEMOS, (user) => user.demo_count),
                        users: toUsers((user) => user.demo_count),
                    },
                    {
                        ...buildGroup(ELeaderboardType.MOST_VISITS, (user) => user.visit_count),
                        users: toUsers((user) => user.visit_count),
                    },
                    {
                        ...buildGroup(
                            ELeaderboardType.MOST_PROPOSALS,
                            (user) => user.proposal_count,
                        ),
                        users: toUsers((user) => user.proposal_count),
                    },
                    {
                        ...buildGroup(
                            ELeaderboardType.MOST_CONVERTED,
                            (user) => user.converted_count,
                        ),
                        users: toUsers((user) => user.converted_count),
                    },
                ]

                return groups
            }),
        )
    }

    getSalesFunnelData(
        userId: string,
        funnelType: ESalesFunnelType = ESalesFunnelType.DEFAULT,
        view = 'user',
    ): Observable<ISalesFunnelApiResponse> {
        const baseUrl = `${this.apiBaseUrl}${salesFunnelEndpoints.SALES_FUNNEL}/${userId}/sales-funnel`
        const params: string[] = []

        if (funnelType !== ESalesFunnelType.DEFAULT) {
            params.push(`type=${encodeURIComponent(funnelType)}`)
        }

        params.push(`view=${encodeURIComponent(view)}`)

        const shouldIncludeAcademicYear = funnelType === ESalesFunnelType.DEFAULT
        const academicYear = this.getSelectedAcademicYearStart()
        if (shouldIncludeAcademicYear && academicYear !== null) {
            params.push(
                `${valueConstants.ACADEMIC_YEAR_QUERY_PARAM}=${encodeURIComponent(
                    String(academicYear),
                )}`,
            )
        }

        const url = `${baseUrl}?${params.join('&')}`

        return this.http.get<ISalesFunnelApiResponse>(url, {
            withCredentials: true,
        }) as Observable<ISalesFunnelApiResponse>
    }

    getDealsPipelineData(userId: string, view = 'user'): Observable<IDealsPipelineApiResponse> {
        const baseUrl = `${this.apiBaseUrl}${salesFunnelEndpoints.SALES_FUNNEL}/${userId}/deals-pipeline?view=${encodeURIComponent(view)}`
        const url = this.appendAcademicYearQueryParam(baseUrl, true)
        return this.http.get<IDealsPipelineApiResponse>(url, {
            withCredentials: true,
        }) as Observable<IDealsPipelineApiResponse>
    }

    private getSelectedAcademicYearStart(): number | null {
        const year = this.selectedAcademicYearStart()
        return Number.isFinite(year) ? year : null
    }

    private appendAcademicYearQueryParam(url: string, enabled: boolean): string {
        if (!enabled) {
            return url
        }
        const year = this.getSelectedAcademicYearStart()
        if (year === null) {
            return url
        }
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}${valueConstants.ACADEMIC_YEAR_QUERY_PARAM}=${encodeURIComponent(
            String(year),
        )}`
    }

    getUserApprovals(
        userId: string,
        options: { page: number; limit: number; status?: ApprovalApiStatus },
    ): Observable<IUserApprovalsApiResponse> {
        const url = `${this.apiBaseUrl}${dashboardEndpoints.USER_APPROVALS_PATH}`.replace(
            '{userId}',
            encodeURIComponent(userId),
        )
        let params = new HttpParams()
            .set('page', String(options.page))
            .set('limit', String(options.limit))
        if (options.status) {
            params = params.set('status', options.status)
        }
        return this.http.get<IUserApprovalsApiResponse>(url, { params })
    }

    getCriticalApprovals(
        userId: string,
        financialYear: number,
        page: number,
        limit: number = criticalApprovalsConstants.PAGE_LIMIT,
    ): Observable<IUserApprovalsApiResponse> {
        const url = `${this.apiBaseUrl}${dashboardEndpoints.USER_APPROVALS_PATH}`.replace(
            '{userId}',
            encodeURIComponent(userId),
        )
        const params = new HttpParams()
            .set('financialYear', String(financialYear))
            .set('status', criticalApprovalsConstants.STATUS_PENDING)
            .set('page', String(page))
            .set('limit', String(limit))
        return this.http.get<IUserApprovalsApiResponse>(url, { params })
    }

    getCriticalApprovalsCountAndOrderIds(
        userId: string,
        financialYear: number,
    ): Observable<{ total: number; orderIds: number[] }> {
        const limit = criticalApprovalsConstants.PAGE_LIMIT
        return this.getCriticalApprovals(userId, financialYear, 1, limit).pipe(
            switchMap((first) => {
                const total = first.total ?? 0
                const firstOrderIds = (first.data ?? []).map((r) => r.order_id)
                if (total <= limit) {
                    return of({ total, orderIds: firstOrderIds })
                }
                const totalPages = Math.ceil(total / limit)
                const remainingPages = Array.from(
                    { length: totalPages - 1 },
                    (_, index) => index + 2,
                )
                const requests = remainingPages.map((page) =>
                    this.getCriticalApprovals(userId, financialYear, page, limit),
                )
                return forkJoin(requests).pipe(
                    map((responses) => {
                        const restOrderIds = responses.flatMap((rest) =>
                            (rest.data ?? []).map((rec) => rec.order_id),
                        )
                        return { total, orderIds: [...firstOrderIds, ...restOrderIds] }
                    }),
                )
            }),
        )
    }
}
